import React from 'react';
import { MoreVertical, Plus, AlertTriangle, Ban, RotateCcw, Copy, Send, CalendarDays } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { fmtGameDate, fmtGameTime, fmtMoney } from '@/lib/officialsApi';
import { AssignmentStatusBadge, OfficialAvatar, StaffingBadge } from './StatusPill';

const AssignmentChip = ({ a, onAction, currency, past }) => (
  <div className={`group flex items-center gap-2 rounded-lg border pl-1.5 pr-1 py-1 bg-white ${a.has_conflict ? 'border-orange-300' : 'border-slate-200'}`}
    data-testid={`assignment-chip-${a.id}`}>
    <OfficialAvatar name={a.official_name} photo={a.photo_path} size="w-6 h-6" />
    <div className="min-w-0">
      <div className="text-xs font-semibold text-slate-800 truncate max-w-[9rem] flex items-center gap-1">
        {a.has_conflict && <AlertTriangle className="w-3 h-3 text-orange-500 shrink-0" />}{a.official_name}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-slate-500">{a.role_label}</span>
        <AssignmentStatusBadge status={a.status} testId={`assignment-status-${a.id}`} />
        {currency && a.fee_minor !== undefined && (
          <span className="text-[10px] font-bold text-emerald-700 tabular-nums" data-testid={`assignment-fee-${a.id}`}>{fmtMoney(a.fee_minor, currency)}</span>
        )}
      </div>
    </div>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid={`assignment-menu-${a.id}`}>
          <MoreVertical className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {a.status !== 'accepted' && <DropdownMenuItem onClick={() => onAction('confirm', a)} data-testid={`assignment-confirm-${a.id}`}>Mark accepted (phone / WhatsApp)</DropdownMenuItem>}
        {a.status === 'pending' && !past && <DropdownMenuItem onClick={() => onAction('resend', a)}>Resend assignment email</DropdownMenuItem>}
        <DropdownMenuItem onClick={() => onAction('replace', a)} data-testid={`assignment-replace-${a.id}`}>Replace official</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction('history', a)} data-testid={`assignment-history-${a.id}`}>View history</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onAction('remove', a)} className="text-red-600" data-testid={`assignment-remove-${a.id}`}>Remove from game</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);

const EmptySlot = ({ label, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:border-[#00247D] hover:text-[#00247D] hover:bg-[#00247D]/5 transition-colors">
    <Plus className="w-3.5 h-3.5" />{label}
  </button>
);

export const GameRow = ({ g, selected, onSelect, onAssign, onAction, onGameAction, canCancel, currency }) => {
  const cancelled = g.game_status === 'cancelled';
  const active = g.assignments.filter(a => ['pending', 'accepted'].includes(a.status));
  const declined = g.assignments.filter(a => a.status === 'declined');
  const slots = g.crew.flatMap(c => {
    const mine = active.filter(a => a.role_key === c.role_key);
    return [...mine.map(a => ({ a })), ...Array.from({ length: Math.max(0, c.count - mine.length) }, () => ({ role: c }))];
  });
  const extra = active.filter(a => !g.crew.some(c => c.role_key === a.role_key));
  const key = `${g.event_id}:${g.match_id}`;
  return (
    <div className={`flex gap-3 px-3 sm:px-4 py-3 border-b last:border-b-0 ${cancelled ? 'bg-slate-50 opacity-70' : 'bg-white'} ${selected ? 'ring-2 ring-inset ring-[#00247D]/30' : ''}`}
      data-testid={`game-row-${g.match_id}`}>
      <Checkbox checked={selected} onCheckedChange={() => onSelect(key)} className="mt-1" data-testid={`game-select-${g.match_id}`} />
      <div className="hidden sm:block w-24 shrink-0">
        <div className="text-xs font-bold text-[#00247D] uppercase">{fmtGameDate(g.date)}</div>
        <div className="text-sm font-semibold text-slate-800">{fmtGameTime(g.time)}</div>
        {g.is_past && !cancelled && <div className="mt-1 inline-flex text-[10px] font-bold uppercase tracking-wide text-slate-500 bg-slate-100 rounded px-1.5 py-0.5" data-testid={`game-played-${g.match_id}`}>Played</div>}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
          <div className="min-w-0 flex-1 basis-[11rem]">
            <div className="sm:hidden text-[11px] font-bold text-[#00247D] uppercase">{fmtGameDate(g.date)} · {fmtGameTime(g.time)}{g.is_past && !cancelled ? ' · Played' : ''}</div>
            <div className={`font-semibold text-slate-900 ${cancelled ? 'line-through' : ''}`}>{g.team_a_name} vs {g.team_b_name}</div>
            <div className="text-xs text-slate-500 truncate">
              {g.event_name}{g.venue ? ` · ${g.venue}` : ''}{g.court ? ` · Court ${g.court}` : ''}{g.label ? ` · ${g.label}` : ''}
            </div>
          </div>
          {currency && g.fee_total_minor > 0 && (
            <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap pt-0.5" data-testid={`game-fee-total-${g.match_id}`}>
              Officials cost <b className="text-slate-800 tabular-nums">{fmtMoney(g.fee_total_minor, currency)}</b>
            </span>
          )}
          <StaffingBadge staffing={g.staffing} filled={g.filled} required={g.required} testId={`game-staffing-${g.match_id}`} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid={`game-menu-${g.match_id}`}>
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!cancelled && <DropdownMenuItem onClick={() => onGameAction('copy', g)} disabled={!active.length} data-testid={`game-copy-crew-${g.match_id}`}><Copy className="w-4 h-4 mr-2" />Copy this crew to…</DropdownMenuItem>}
              {!cancelled && !g.is_past && <DropdownMenuItem onClick={() => onGameAction('notify', g)} disabled={!active.some(a => a.status === 'pending')}><Send className="w-4 h-4 mr-2" />Resend pending emails</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => onGameAction('open', g)}><CalendarDays className="w-4 h-4 mr-2" />Open schedule</DropdownMenuItem>
              {canCancel && (cancelled
                ? <DropdownMenuItem onClick={() => onGameAction('restore', g)} data-testid={`game-restore-${g.match_id}`}><RotateCcw className="w-4 h-4 mr-2" />Restore game</DropdownMenuItem>
                : <DropdownMenuItem onClick={() => onGameAction('cancel', g)} className="text-red-600" data-testid={`game-cancel-${g.match_id}`}><Ban className="w-4 h-4 mr-2" />Cancel game</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {!cancelled && (
          <div className="flex flex-wrap gap-2">
            {slots.map((s, i) => s.a
              ? <AssignmentChip key={s.a.id} a={s.a} currency={currency} past={g.is_past} onAction={(t, a) => onAction(t, a, g)} />
              : <EmptySlot key={`e${i}`} label={s.role.label} onClick={() => onAssign(g, s.role.role_key)} testId={`assign-slot-${g.match_id}-${s.role.role_key}-${i}`} />)}
            {extra.map(a => <AssignmentChip key={a.id} a={a} currency={currency} past={g.is_past} onAction={(t, x) => onAction(t, x, g)} />)}
            {!g.crew.length && <EmptySlot label="Official" onClick={() => onAssign(g, 'referee')} testId={`assign-slot-${g.match_id}-any`} />}
          </div>
        )}
        {declined.length > 0 && !cancelled && (
          <div className="text-xs text-red-600" data-testid={`game-declined-${g.match_id}`}>
            Declined: {declined.map(a => `${a.official_name} (${a.role_label})`).join(', ')}
          </div>
        )}
      </div>
    </div>
  );
};
